import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

const emptyConfig = {
  provider: 'vertex',
  model_name: '',
  api_key_env_var: 'GEMINI_API_KEY',
  extra_config: {},
};

const LLMProviderSettings = () => {
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(emptyConfig);
  const [models, setModels] = useState([]);
  const [modelsSource, setModelsSource] = useState('api');
  const [loading, setLoading] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(0);

  const activeProvider = useMemo(
    () => providers.find((item) => item.id === config.provider) || null,
    [providers, config.provider],
  );
  const modelOptions = useMemo(() => {
    const list = Array.isArray(models) ? models : [];
    if (config.model_name && !list.includes(config.model_name)) {
      return [config.model_name, ...list];
    }
    return list;
  }, [models, config.model_name]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [providersRes, configRes] = await Promise.all([
          api.get('/api/admin/llm/providers'),
          api.get('/api/admin/llm/config'),
        ]);
        if (!active) return;

        const providerList = Array.isArray(providersRes.data) ? providersRes.data : [];
        setProviders(providerList);

        const serverConfig = configRes.data || {};
        setConfig({
          provider: serverConfig.provider || 'vertex',
          model_name: serverConfig.model_name || '',
          api_key_env_var: serverConfig.api_key_env_var || 'GEMINI_API_KEY',
          extra_config: serverConfig.extra_config || {},
        });
      } catch {
        toast.error('Failed to load LLM provider settings');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!config.provider) return;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        const { data } = await api.get(`/api/admin/llm/models/${config.provider}`);
        if (requestId !== requestIdRef.current) return;

        const modelList = Array.isArray(data?.models) ? data.models : [];
        setModels(modelList);
        setModelsSource(data?.source || 'api');

        setConfig((prev) => {
          if (prev.model_name) return prev;
          return { ...prev, model_name: modelList[0] || prev.model_name };
        });
      } catch {
        if (requestId !== requestIdRef.current) return;
        setModels([]);
        setModelsSource('fallback');
        toast.error('Failed to load models for selected provider');
      } finally {
        if (requestId === requestIdRef.current) setLoadingModels(false);
      }
    };

    fetchModels();
  }, [config.provider]);

  const handleProviderChange = (event) => {
    const nextProvider = event.target.value;
    const providerMeta = providers.find((item) => item.id === nextProvider);
    setConfig((prev) => ({
      ...prev,
      provider: nextProvider,
      api_key_env_var: providerMeta?.key_env_var || prev.api_key_env_var,
      model_name: '',
    }));
  };

  const handleSave = async () => {
    if (!activeProvider?.key_configured) return;
    setSaving(true);
    try {
      const payload = {
        provider: config.provider,
        model_name: config.model_name,
        api_key_env_var: config.api_key_env_var,
        extra_config: config.extra_config || {},
      };
      const { data } = await api.put('/api/admin/llm/config', payload);
      setConfig((prev) => ({
        ...prev,
        provider: data.provider,
        model_name: data.model_name,
        api_key_env_var: data.api_key_env_var,
        extra_config: data.extra_config || {},
      }));
      toast.success('LLM provider settings saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save LLM provider settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="muted">Loading LLM provider settings...</p>;
  }

  return (
    <section className="admin-llm-config">
      <h3>Provider Configuration</h3>
      <p className="muted">Select the active provider/model used for merges and revisions.</p>

      <div className="form-group">
        <label htmlFor="llm-provider-select">Provider</label>
        <select
          id="llm-provider-select"
          className="form-control"
          value={config.provider}
          onChange={handleProviderChange}
        >
          {providers.map((provider) => (
            <option key={provider.id} value={provider.id}>
              {provider.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="llm-model-select">Model</label>
        <select
          id="llm-model-select"
          className="form-control"
          value={config.model_name}
          disabled={loadingModels || modelOptions.length === 0}
          onChange={(event) => setConfig((prev) => ({ ...prev, model_name: event.target.value }))}
        >
          {modelOptions.map((model) => (
            <option key={model} value={model}>
              {model}
            </option>
          ))}
        </select>
        {loadingModels && <p className="muted">Loading model list...</p>}
        {modelsSource === 'fallback' && (
          <span className="badge badge--medium">Live model list unavailable - showing known models</span>
        )}
      </div>

      <div className="form-group">
        <label>API key source</label>
        <p>
          <code>{config.api_key_env_var}</code>{' '}
          <span className={`badge ${activeProvider?.key_configured ? 'badge--low' : 'badge--high'}`}>
            {activeProvider?.key_configured ? 'Configured' : 'Missing'}
          </span>
        </p>
      </div>

      <div className="form-actions">
        <button
          className="btn btn--primary"
          type="button"
          onClick={handleSave}
          disabled={saving || !config.model_name || !activeProvider?.key_configured}
        >
          {saving ? 'Saving...' : 'Save LLM Config'}
        </button>
      </div>
    </section>
  );
};

export default LLMProviderSettings;
